// ABOUTME: The pieces every job page is drawn with: a section (blue bold title on the page's background, no card), a
// ABOUTME: label/value row, the notice box, and the fixed footer the apply button sits in. The pages of an application
// ABOUTME: and of my job use the same ones, so the parts they add read as if they were always part of the job page.
import { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import { FloatingBlurredLayout } from 'components/layouts/FloatingBlurredLayout';
import { Text } from 'components/Text';

/**
 * One section of the job page. With `headingId` the section is a named region (the title labels it), which the parts
 * the application and my job add use; the listing's own sections stay unnamed, as they were.
 */
export const JobPageSection = ({
  title,
  testId,
  headingId,
  children
}: {
  title: string;
  testId?: string;
  headingId?: string;
  children: ReactNode;
}) => (
  <Box
    component="section"
    data-testid={testId}
    aria-labelledby={headingId}
    sx={{ mb: 3 }}
  >
    <Text
      id={headingId}
      variant="subtitle1"
      component="h2"
      color="primary.main"
      sx={{ fontWeight: 700, mb: 1 }}
    >
      {title}
    </Text>
    {children}
  </Box>
);

/** A label on the left, its value bold on the right. */
export const JobPageRow = ({
  label,
  value,
  testId,
  valueTestId
}: {
  label: string;
  value: ReactNode;
  testId?: string;
  valueTestId?: string;
}) => (
  <Box
    data-testid={testId}
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
  >
    <Text variant="body2" color="text.secondary">
      {label}
    </Text>
    <Text
      variant="body2"
      color="text.primary"
      data-testid={valueTestId}
      sx={{ fontWeight: 600, textAlign: 'right' }}
    >
      {value}
    </Text>
  </Box>
);

/** The tinted box the job page explains things in (the location check, the working time, an open-ended contract). */
export const JobPageNotice = ({
  title,
  body,
  severity = 'info',
  testId,
  dataType
}: {
  title?: string;
  body?: ReactNode;
  severity?: 'info' | 'warning' | 'success';
  testId?: string;
  dataType?: string;
}) => (
  <Alert
    data-testid={testId}
    data-type={dataType}
    severity={severity}
    icon={false}
    sx={{ borderRadius: 2, mb: 1 }}
  >
    {title && (
      <AlertTitle sx={{ fontWeight: 700, mb: body ? 0.5 : 0 }}>
        {title}
      </AlertTitle>
    )}
    {body}
  </Alert>
);

/**
 * The foot of a job page, fixed above the navigation: where "Bewerben" sits on a listing and "Bewerbung zurückziehen"
 * on an application. The page leaves a seam under its last section so nothing hides behind it.
 */
export const JobPageFooter = ({
  testId,
  children
}: {
  testId?: string;
  children: ReactNode;
}) => (
  <FloatingBlurredLayout>
    <Box
      data-testid={testId}
      sx={{ width: '100%', maxWidth: 480, px: 2, display: 'grid', gap: 1 }}
    >
      {children}
    </Box>
  </FloatingBlurredLayout>
);
